import {
  CategoryType,
  CollectionType,
  CommentType,
  ConversationType,
  ExternalProductSearch,
  FeedType,
  GamificationType,
  GeoPointType,
  LevelType,
  MediaType,
  MessageType,
  ProductDetailsType,
  ProductOfCategory,
  ProductType,
  RatingType,
  RecommendationType,
  UserType,
} from '@/@types';
import { PeoopleError } from '@/utils/error';
import configuration from '@/configuration';
import { useEvent } from '@/components/context/events';
import { categories, findCategoryBySubCategory, gamification as gamificationMapper } from '@/@constants';
import Geohash from 'latlon-geohash';
import * as parsers from './parsers';
import { CDNImage, isProductOfType } from './parsers/utils';
import spotify from './externals/spotify';
import movieDB from './externals/moviedb';
import places from './externals/places';
import { User } from './namespaces/user';
import { Category } from './namespaces/category';
import { getGraphlClient } from './namespaces/_utils';
import { Product } from './namespaces/product';
import { Recommendation } from './namespaces/recommendation';

type GenericAbortSignal = AbortSignal;

type RequestParamsType = {
  method?: 'get' | 'post' | 'put' | 'delete';
  url: string;
  data?: Object;
  token?: string;
  signal?: GenericAbortSignal;
  params?: any;
};

const sleep = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export { gql } from './namespaces/_utils';

export const request = async ({ method = 'get', url, data, params, token, signal }: RequestParamsType) => {
  let tries = 0;
  const makeRequest = () => {
    const cleanParams = Object.fromEntries(
      Object.entries(params || {}).filter(([, value]) => {
        return value !== undefined;
      }),
    );

    return fetch(`${configuration.api}${url}?${new URLSearchParams(cleanParams as any).toString()}`, {
      method,
      signal,
      body: JSON.stringify(data),
      headers: {
        // TODO: Fix this hack
        ...(token ? { Authorization: `Bearer ${token}` } : { 'request-from': 'web' }),
      },
    })
      .then((res) => {
        // return 404 on empty response on get requests (Invalid API Response)
        if (method === 'get' && res.headers.get('Content-Length') === '0') {
          return PeoopleError('empty', 404);
        }

        if (!res.ok) {
          return res
            .json()
            .catch(() => {
              return { statusCode: res.status, message: res.statusText };
            })
            .then((err) => {
              return Promise.reject(PeoopleError(err.message, err.statusCode));
            });
        }

        return res.headers.get('content-type')?.includes('application/json') ? res.json() : res.text();
      })
      .catch((e) => {
        if (e.statusCode === 404) {
          return undefined;
        }
        tries += 1;
        if (e.statusCode >= 500 && tries <= 3) {
          return sleep(500).then(makeRequest);
        }
        console.debug(`[${method}] ${url} [${e.statusCode}] ${e.message} after Tries; ${tries}`);
        // 502 504 are ussually due api being down, 500 could be due an error on the request (expected uuid)

        return Promise.reject(PeoopleError(e?.message, e.statusCode));
      });
  };
  return makeRequest();
};

export const login = async ({ email, password }: { email: string; password: string }) => {
  const res = (await request({
    method: 'post',
    url: '/api/auth/login/email',
    data: { email, password },
  })) as UserType & { token: string };
  return res;
};

export const register = async (service: 'google' | 'email', userInfo: any) => {
  const endpoints = {
    email: '/api/auth/registration/email',
    google: '/api/auth/login/google',
  };
  const { email, password, name, nickname, surname, timezone, google } = userInfo;

  return request({
    url: endpoints[service],
    method: 'post',
    data: {
      email,
      password,
      nombre: name,
      nickname: nickname || [name, surname].map(Boolean).join(''),
      apellido: surname || '',
      checkNickname: true,
      defaultCollections: false,
      inAppInvite: true,
      timezone,
      accept_policy: true,
      accept_marketing: false,
      ...(google && {
        googleUserID: google.id,
        googleUserToken: google.idToken,
      }),
      lang: 'en',
      // hardcoded values
      deviceName: 'web',
      platform: 'web',
      playerId: '00',
      deviveId: '001',
      macAddress: '00',
      deviceToken: '00',
      deviceId: `00${Math.floor(Math.random() * 1000)}`,
    },
  });
};

export const API = ({
  token,
  user: currentUser,
  emitter,
  locale,
}: { token?: string; user?: UserType; emitter?: ReturnType<typeof useEvent>; geohash?: string; locale?: string; location?: GeoPointType } = {}) => {
  const authenticatedRequest = (args: RequestParamsType) => {
    return request({
      token,
      ...args,
    });
  };

  const cache = new Map();

  const getSpotifyKey = async () => {
    if (!cache.has('spotifyKey')) {
      const { spotifyKey } = await authenticatedRequest({
        url: '/api/providers/spotify',
      });
      cache.set('spotifyKey', spotifyKey);
    }
    return cache.get('spotifyKey');
  };

  const checkNicknameAvailability = (
    nickname: string,
  ): Promise<{
    isValid: boolean;
    isTaken: boolean;
  }> => {
    return authenticatedRequest({
      url: '/users/nickname/check',
      params: {
        nickname,
      },
    });
  };

  const getCurrentUser = async () => {
    if (currentUser) {
      return currentUser;
    }
    try {
      const [{ user }, { userLocations: locations }] = await Promise.all([
        authenticatedRequest({
          url: '/users/me',
        }),
        authenticatedRequest({
          url: '/users/me/locations',
        }),
      ]);
      return {
        ...parsers.V1.user(user),
        geoHashes: locations
          .map((location: any) => {
            return location.geohash;
          })
          .filter(Boolean),
      };
    } catch {
      return undefined;
    }
  };

  /**
   *
   * @param productId Optional productId to check if that product belown to any of the collections
   */
  const getCurrentUserCollections = (productId?: string) => {
    return authenticatedRequest({
      url: '/products/collections',
      params: {
        productId,
      },
    }).then(({ collections, collectionsSaved }) => {
      const collectionsSavedIds = collectionsSaved.map(({ id }: { id: string }) => {
        return id;
      });
      return (collections as any[]).map((col) => {
        const collection = parsers.V1.collection(col);
        return {
          ...collection,
          contentFeed: collectionsSavedIds.includes(collection.id) ? [{ product: { id: productId } }] : [],
        } as CollectionType;
      });
    });
  };

  const search = async (query: string, { signal }: { signal?: GenericAbortSignal } = {}) => {
    try {
      const getType = (item: any) => {
        if (item.nickname) {
          return 'user';
        }
        if (item.owner) {
          return 'collection';
        }
        if (item.profesional_subcategoria_id) {
          return 'product';
        }
        return undefined;
      };

      const response = (
        await authenticatedRequest({
          url: '/search/all',
          params: {
            query,
          },
          signal,
        })
      ).searchResults;
      const removeTypeUndefined = (x) => {
        return !!x.type;
      };
      return {
        topResult: response.topResult
          .map((item: any) => {
            const type = getType(item);
            if (!type) {
              return undefined;
            }
            return { type, data: parsers.V1[type](item) };
          })
          .filter(Boolean),
        users: response.users
          .map((item: any) => {
            return { type: getType(item), data: parsers.V1.user(item) };
          })
          .filter(removeTypeUndefined),
        collections: response.collections
          .map((item: any) => {
            return { type: getType(item), data: parsers.V1.collection(item) };
          })
          .filter(removeTypeUndefined),
        products: response.products
          .map((item: any) => {
            return { type: getType(item), data: parsers.V1.product(item) };
          })
          .filter(removeTypeUndefined),
      };
    } catch {
      return {
        collections: [],
        products: [],
        users: [],
        topResult: [],
      };
    }
  };

  const searchByCategory = async (category: keyof typeof categories, query: string, { location }: any = {}): Promise<ExternalProductSearch[]> => {
    const type = query ? 'search' : 'suggestions';
    const geohashes = location ? Geohash.encode(location.latitude, location.longitude) : undefined;

    const spotifyToken = await getSpotifyKey();
    const spotifyApi = spotify(spotifyToken);

    const movieDBAPI = movieDB();

    const searchMapper = {
      restaurants: {
        suggestions: async () => {
          return authenticatedRequest({
            url: `/products/category/${category}`,
            params: {
              geohashes: geohashes || currentUser?.geoHashes?.join(','),
              limit: 18,
              orderby: 'relevance',
            },
          }).then((res) => {
            const x = res.object.data
              .map(parsers.V1.product)
              .filter(Boolean)
              .map((parsed) => {
                return {
                  ...parsed,
                  details: parsed.address,
                };
              });
            return x;
          });
        },
        search: async () => {
          return places().search({ query, location });
        },
      },
      music: {
        suggestions: async () => {
          return spotifyApi.suggestions();
        },
        search: async () => {
          return spotifyApi.search({ query });
        },
      },
      movies: {
        suggestions: async () => {
          return movieDBAPI.suggestions({ type: 'movie' });
        },
        search: async () => {
          return movieDBAPI.search({ type: 'movie', query });
        },
      },
      series: {
        suggestions: async () => {
          return movieDBAPI.suggestions({ type: 'tv' });
        },
        search: async () => {
          return movieDBAPI.search({ type: 'tv', query });
        },
      },
    };
    const cacheKey = `search-${category}-${type}-${query}-${geohashes}`;

    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }

    if (searchMapper[category]?.[type]) {
      const results = (await searchMapper[category][type]()) as ExternalProductSearch[];
      cache.set(cacheKey, results);
      return results;
    }

    return authenticatedRequest({
      url: '/v1.0/search',
      params: {
        text: query,
        category,
        size: 20,
      },
    }).then(({ products }) => {
      return products.map(parsers.V1.product);
    });
  };

  const exploreNear = async (params: {
    latitud: number;
    longitud: number;
    delta?: number;
    filters?: { users?: string; subcategories?: string[] };
  }) => {
    return authenticatedRequest({
      url: `/products/discover`,
      params: {
        currentUser: currentUser?.id,
        latitudeDelta: params.delta || 0.02,
        longitudeDelta: params.delta || 0.02,
        lat: params.latitud,
        lng: params.longitud,
        subcategories: params.filters?.subcategories?.join(','),
      },
    }).then(({ products }) => {
      return products.map((product: any) => {
        return {
          ...parsers.V1.product(product),
          comments: [
            {
              id: '1',
              date: new Date(),
              liked: false,
              likes: 0,
              message: product.truster1_message,
              user: {
                id: product.truster1_id,
                name: product.truster1_username,
                nickname: product.truster1_nickname,
                avatar: CDNImage(product.truster1),
                surname: product.user_1_usersurname,
                level: gamificationMapper[product.truster1_level],
              },
            },
          ],
        } as ProductType;
      }) as ProductOfCategory<'restaurants' | 'places'>[];
    });
  };

  const toggleFollow = async (userId: string, isFollowed = false) => {
    return authenticatedRequest({
      url: '/follows/create',
      method: 'put',

      data: {
        trusterId: userId,
        wantsToFollow: !isFollowed,
        fromSearch: false,
      },
    }).then(() => {
      emitter?.dispatchEvent('follow', { userId, isFollowed: !isFollowed });
    });
  };

  const like = async (commentId: string, owner?: string, productId?: string) => {
    const user = await getCurrentUser();
    return authenticatedRequest({
      url: `/recommendations/${commentId}/likes/`,
      method: 'post',
      params: {
        productId,
        receiverId: owner,
        userId: user?.id,
      },
    });
  };

  const getRatings = async (productId: string, { signal }: { signal?: GenericAbortSignal } = {}) => {
    return authenticatedRequest({
      url: `/v2/product/${productId}/ratings`,
      signal,
    })
      .then((res) => {
        return [
          {
            source: 'imdb',
            value: res.imdbRating,
            total: 10,
            votes: (res.imdbVotes || '').replace(/,/g, ''),
          },
          {
            source: 'metacritic',
            value: res.metascore,
            total: 100,
          },
          {
            source: 'rottentomatoes',
            value: res.rottenTomatoesScore,
            total: 0,
            unit: '%',
          },
        ].filter((x) => {
          return !!x.value && x.value !== 'N/A';
        }) as RatingType[];
      })
      .catch(() => {
        return [];
      });
  };

  const getUserCollections = async (userId: string) => {
    return authenticatedRequest({
      url: `/users/${userId}/collections`,
    }).then(({ collections }) => {
      return collections.map(parsers.V1.collection) as CollectionType[];
    });
  };

  const getUserInfo = async (userId: string) => {
    return authenticatedRequest({
      url: `/users/${userId}/profilev2`,
    }).then(({ user }) => {
      // TODO: Fix case when user is undefined?
      return parsers.V1.user({ ...user, userId });
    });
  };

  const getUserProfile = async (userId: string) => {
    const profilePromise = getUserInfo(userId);

    const collectionPromise = authenticatedRequest({
      url: `/users/${userId}/collections`,
    }).then(({ collections }) => {
      return collections.map(parsers.V1.collection);
    });

    const [profile, collections] = await Promise.allSettled([profilePromise, collectionPromise]);

    return {
      ...(profile as PromiseFulfilledResult<any>).value,
      collectionArray: (collections as PromiseFulfilledResult<CollectionType[]>).value.map((c) => {
        return {
          ...c,
          owner: (profile as PromiseFulfilledResult<any>).value,
        };
      }),
    } as UserType;
  };

  const getUserIdByNickname = async (nickname: string): Promise<string | undefined> => {
    const res = await authenticatedRequest({
      url: `/v2/user/getbynickname/@${nickname}`,
    });
    if (!res?.id) {
      const withoutAt = await authenticatedRequest({
        url: `/v2/user/getbynickname/${nickname}`,
      });
      return withoutAt?.id;
    }
    return res?.id;
  };

  const getUserProfileByNickname = async (nickname: string): Promise<UserType | undefined> => {
    const id = await getUserIdByNickname(nickname);
    if (!id) {
      return undefined;
    }
    return getUserProfile(id);
  };

  const getComments = async (productId: string, { page = 0, signal }: { page?: number; signal?: GenericAbortSignal } = {}) => {
    const { isOnMyCollections, ...comments } = await authenticatedRequest({
      url: `/products/${productId}/timelines`,
      signal,
      params: {
        requests: page,
      },
    }).catch((err) => {
      console.log(err);
      return { timelines: [] };
    });
    return comments.timelines.map(parsers.V1.comment);
  };

  const getRecommendationById = async (recommendationId: string) => {
    return authenticatedRequest({
      url: `/v2/recommendation/getById/${recommendationId}`,
    })
      .then((recommendation) => {
        return recommendation;
      })
      .catch(() => {
        return undefined;
      });
  };

  const getProductBasic = async (
    productId: string,
    { signal, referer }: { referer?: string; signal?: GenericAbortSignal } = {},
  ): Promise<ProductType | undefined> => {
    let refererPromise = Promise.resolve<any>(undefined);
    const productPromise = authenticatedRequest({
      url: `/v2/product/new/${productId}`,
      signal,
      params: {
        recommendationId: referer,
      },
    })
      .catch((e: ReturnType<typeof PeoopleError>) => {
        // handle api issue that return 500 instead of 404 when product do not exists
        // also avoit 400 error and show 404 instead
        if (e.statusCode <= 500) {
          return undefined;
        }
        return Promise.reject(e);
      })
      .then(parsers.V2.product);

    if (referer) {
      refererPromise = getRecommendationById(referer);
    }

    const [product, refererData] = await Promise.all([productPromise, refererPromise]);

    if (!product) {
      return undefined;
    }

    if (refererData) {
      return {
        ...product,
        media: [
          { type: 'video', source: refererData.video },
          { type: 'image', source: CDNImage(refererData.image) },
        ]
          .concat(product.media)
          .filter((x) => {
            return x.source;
          }) as MediaType[],
        recommendations: {
          ...product.recommendations,
          items: [
            {
              id: refererData.id,
              message: refererData.message || '',
              user: await getUserInfo(refererData.userSourceId),
              date: new Date(refererData.date),
              createdAt: new Date(refererData.date).toUTCString(),
              likes: refererData.likes || 0,
              product,
              media: [
                {
                  type: 'video',
                  source: refererData.video,
                },
                {
                  type: 'image',
                  source: CDNImage(refererData.image),
                },
              ].filter((x) => {
                return x.source;
              }) as RecommendationType['media'],
            },
          ].concat(product.recommendations.items || []),
        },
      };
    }

    return product;
  };

  const getProduct = async (
    productId: string,
    { signal, referer }: { referer?: string; signal?: GenericAbortSignal } = {},
  ): Promise<ProductDetailsType | undefined> => {
    const mainProductInfo = await getProductBasic(productId, { signal, referer });

    if (!mainProductInfo) {
      return undefined;
    }

    // optional call for more data, do not fail on error
    const extraInformation = authenticatedRequest({
      url: `/v2/product/${productId}`,
      signal,
    })
      .then((oldProductData) => {
        return {
          image: CDNImage(oldProductData.image),
          ...(oldProductData.firstAirDate && { releaseDate: new Date(oldProductData.firstAirDate) }),
        };
      })
      .catch(() => {
        return {};
      });

    const relatedProductsPromise = authenticatedRequest({
      url: `/products/${productId}/related`,
      signal,
      params: {
        limit: 6,
      },
    })
      .then((res) => {
        return res?.products.map(({ item }: any) => {
          return {
            ...parsers.V1.product(item),
            users: item.users.map(parsers.V1.user),
          };
        });
      })
      .catch(() => {
        return [];
      });

    const ratingsPromise = isProductOfType(mainProductInfo, ['movies', 'series']) ? getRatings(productId, { signal }) : Promise.resolve([]);

    const commentsPromise = getComments(productId, { signal });

    const friendsRecommendationsPromise = !token
      ? Promise.resolve([])
      : authenticatedRequest({
          url: `/products/${productId}/recommendations/friends`,
          signal,
        })
          .then(({ friends }) => {
            return friends.map(parsers.V1.user);
          })
          .catch(() => {
            return [];
          });

    const response = await Promise.allSettled([
      extraInformation,
      relatedProductsPromise,
      commentsPromise,
      ratingsPromise,
      friendsRecommendationsPromise,
    ]);

    const [extra, relatedProducts, comments, ratings, friendsRecommendations] = response;

    return {
      ...(extra as any).value,
      ...mainProductInfo,
      relatedProducts: (relatedProducts as any).value || [],
      comments: ((comments as any).value as CommentType[]) || [],
      ratings: mainProductInfo.ratings.concat((ratings as any).value).filter((rating) => {
        return ![null, undefined].includes(rating.value as any);
      }),
      friendsRecommendations: (friendsRecommendations as any).value,
    } as ProductDetailsType;
  };

  const getProductList = async (
    { offset = 0, limit = 10 }: { offset?: number; limit?: number } = {},
    { signal }: { signal?: GenericAbortSignal } = {},
  ): Promise<{
    total: number;
    items: ProductType[];
  }> => {
    return authenticatedRequest({
      url: '/v2/product/getProducts/all',
      params: {
        offset,
        limit,
      },
      signal,
    }).then((response) => {
      return {
        total: response.total,
        items: response.items
          .filter((item) => {
            return item.name && item.subcategoryId;
          })
          .map((item) => {
            const category = findCategoryBySubCategory(item.subcategoryId.toString());
            return {
              id: item.id,
              name: item.name,
              category: {
                id: category.id,
                name: category.name,
              },
              updatedAt: new Date(item.updateAt),
            } as ProductType;
          }),
      };
    });
  };

  const getUserList = async (
    { offset = 0, limit = 10 }: { offset?: number; limit?: number } = {},
    { signal }: { signal?: GenericAbortSignal } = {},
  ): Promise<{
    total: number;
    items: { nickname: string; level: LevelType }[];
  }> => {
    return authenticatedRequest({
      url: '/v2/user/getUsers/all',
      params: {
        offset,
        limit,
      },
      signal,
    }).then((response) => {
      return {
        total: response.total,
        items: response.items
          .filter((item) => {
            return item.nickname;
          })
          .map((item) => {
            return {
              nickname: item.nickname,
              level: gamificationMapper[item.level],
            };
          }),
      };
    });
  };

  const getCollectionContent = (collectionId, { offset }: { offset?: number } = {}): Promise<{ hasMoreData: boolean; contentFeed: FeedType[] }> => {
    return authenticatedRequest({
      url: `/v2/ContentCollection/${collectionId}`,
      params: {
        offset,
      },
    }).then((res) => {
      if (!res) {
        return { contentFeed: [], hasMoreData: false };
      }
      const { contentCollections, hasMoreData } = res;
      const contentFeed = contentCollections.map(({ product, recommendation }) => {
        return {
          product: parsers.V2.product(product, recommendation),
          comment: recommendation ? parsers.V2.comment(recommendation) : undefined,
        } as FeedType;
      });
      return { contentFeed, hasMoreData };
    });
  };

  const getCollection = async (collectionId: string): Promise<CollectionType | undefined> => {
    const infoPromise = authenticatedRequest({
      url: `/collections/${collectionId}`,
    }).then(({ collection }) => {
      return parsers.V1.collection(collection);
    });

    const contentPromise = getCollectionContent(collectionId);
    try {
      const [info, content] = await Promise.all([infoPromise, contentPromise]);
      return {
        ...info,
        ...content,
      };
    } catch {
      return undefined;
    }
  };

  const createProduct = async (
    product:
      | { externalId: string; category: CategoryType; name?: never; url?: never }
      | { name: string; url: string; category: CategoryType; externalId?: never },
  ) => {
    const mapper = {
      movies: 'geniusId',
      series: 'geniusId',
      music: 'spotifyId',
      restaurants: 'googlePlacesId',
    };
    return authenticatedRequest({
      url: `/v2/product/${product.category.name}/create`,
      method: 'put',
      data: {
        ...(product.externalId
          ? { [mapper[product.category.name]]: product.externalId }
          : {
              name: product.name,
              url: product.url,
            }),
      },
    }).then((res) => {
      return { id: res } as { id: string };
    });
  };

  const createCollection = async ({ name, isPrivate }: { name: string; isPrivate: boolean }) => {
    return authenticatedRequest({
      method: 'post',
      url: '/collections',
      data: {
        name,
        isPrivate,
      },
    }).then(({ collection }) => {
      return parsers.V1.collection(collection);
    });
  };

  const addProductToCollection = async ({ productId, collectionId }: { productId: string; collectionId: string }) => {
    emitter?.dispatchEvent('toggle-product-collection', { productId, collectionId, isSaved: true });
    return authenticatedRequest({
      method: 'post',
      url: '/contentCollections',
      data: {
        productId,
        collectionIds: [collectionId],
      },
    })
      .then(({ contentCollections }) => {
        return contentCollections;
      })
      .catch(() => {
        emitter?.dispatchEvent('toggle-product-collection', { productId, collectionId, isSaved: false });
      });
  };

  const removeProductFromCollection = ({ productId, collectionId }: { productId: string; collectionId: string }) => {
    emitter?.dispatchEvent('toggle-product-collection', { productId, collectionId, isSaved: false });
    return authenticatedRequest({
      method: 'delete',
      url: `/v2/contentCollection/${collectionId}/${productId}`,
    }).catch(() => {
      emitter?.dispatchEvent('toggle-product-collection', { productId, collectionId, isSaved: true });
      return undefined;
    });
  };

  const getNotifications = async ({ limit = 10, offset = 0 }: { limit?: number; offset?: number } = {}) => {
    const { notifications, hasMoreData } = await authenticatedRequest({
      url: '/v2/notification/recent',
      params: { take: limit, offset },
    });

    const getUserId = (item) => {
      return item.data.broadcastByUserId || item.data.likedByUserId || item.data.followedByUserId || item.data.savedByUserId;
    };

    const users = Object.keys(
      notifications.reduce((acc, current) => {
        const id = getUserId(current);
        if (!id) {
          return acc;
        }
        return {
          ...acc,
          [id]: 1,
        };
      }, {}),
    );

    const usersInfo = (await Promise.all(users.map(getUserInfo))).reduce((acc, current) => {
      return {
        ...acc,
        [current.id]: current,
      };
    }, {});

    return {
      items: notifications.map((item) => {
        return {
          ...item,
          date: new Date(item.date),
          user: usersInfo[getUserId(item)],
        };
      }),
      hasMore: hasMoreData as Boolean,
    };
  };

  const markNotificationsAsRead = async (ids: string[] = []) => {
    return authenticatedRequest({
      url: '/v2/notification/markAsRead',
      method: 'post',
      data: { ids },
    });
  };

  const getUserActivity = async (userId: string, { offset = 0 }: { offset?: number } = {}): Promise<FeedType[]> => {
    return authenticatedRequest({
      url: `/v2/recommendation/getByUser/${userId}`,
      params: {
        offset,
      },
    }).then((activities) => {
      return activities.map(parsers.V2.feed).filter(Boolean);
    });
  };

  const isMonetizableURL = (url): Promise<{ isMonetizable: boolean; url: string }> => {
    return authenticatedRequest({
      url: '/v2/wallet/urlMonetizable',
      params: {
        url,
      },
    });
  };

  const getGamificationProgress = (userId: string) => {
    return authenticatedRequest({
      url: `/users/${userId}/gamification/challengesprogress`,
    }).then(({ data }) => {
      return parsers.V1.gamification(data) as GamificationType;
    });
  };

  // TODO: Update this endpoint when new process is complete
  const createRecommendation = ({
    productId,
    collectionId,
    message,
    file,
  }: {
    productId: string;
    collectionId: string;
    message?: string;
    file: any;
  }) => {
    const data = new FormData();

    data.append('productId', productId);
    data.append('collectionId', collectionId);

    if (message) {
      data.append('message', message);
    }
    if (file) {
      data.append('file', file);
    }

    return authenticatedRequest({
      url: '/v2/recommendation/',
      method: 'post',
      data,
    });
  };

  const createConversation = async (userId: string): Promise<ConversationType> => {
    return authenticatedRequest({
      url: '/v2/dm/create',
      method: 'post',
      data: {
        receivers: [userId],
      },
    }).then(parsers.V2.conversation);
  };

  const markConversationAsRead = async (conversation: ConversationType) => {
    const lastMessage = conversation.messages.at(-1);
    if (!lastMessage) {
      return null;
    }
    await authenticatedRequest({
      url: `/v2/dm/${conversation.id}/message/${lastMessage.id}/seen`,
      method: 'post',
    });
    return null;
  };

  const getConversations = async (): Promise<ConversationType[]> => {
    return authenticatedRequest({
      url: '/v2/dm/list',
    }).then(({ conversations }) => {
      return conversations.map((conversation) => {
        return parsers.V2.conversation(conversation, conversation.lastMessage ? [conversation.lastMessage] : undefined);
      });
    });
  };

  const getConversation = async (conversationId: string, { cursor }: { cursor?: number } = {}): Promise<ConversationType> => {
    return Promise.all([
      authenticatedRequest({
        url: `/v2/dm/${conversationId}`,
      }),
      authenticatedRequest({
        url: `/v2/dm/${conversationId}/message`,
        params: {
          limit: 20,
          cursor,
          direction: 'before',
        },
      }),
    ]).then(([conversation, { messages }]) => {
      return parsers.V2.conversation(conversation, messages);
    });
  };

  const sendMessage = async ({ conversationId, text }: { conversationId: string; text: string }): Promise<MessageType> => {
    return authenticatedRequest({
      url: `/v2/dm/${conversationId}/message`,
      method: 'post',
      data: {
        data: {
          text,
        },
      },
    }).then((menssage) => {
      return parsers.V2.message(menssage, { [currentUser?.id!]: currentUser! });
    });
  };

  const getUserRelations = async ({ userId, type, offset = 0 }: { userId: string; type: 'followers' | 'follows'; offset?: number }) => {
    const pageSize = 20;
    return authenticatedRequest({
      url: `/v2/user/${userId}/relations`,
      params: {
        type,
        maxItems: pageSize,
        page: Math.ceil(offset / pageSize),
      },
    }).then((res) => {
      const data = res[type];
      return {
        ...data,
        list: data.list.map(parsers.V2.user),
      } as {
        hasMoreItems: boolean;
        total: number;
        list: UserType[];
      };
    });
  };

  const updateProfile = (userId: string, user: Partial<UserType>) => {
    // Inverse parse user to V1
    // remove from object empty values to avoid put to unset values not updated
    const website = user.links?.find((link) => {
      return link.type === 'tiktok';
    })?.name;
    const instagram = user.links?.find((link) => {
      return link.type === 'instagram';
    })?.name;
    const youtube = user.links?.find((link) => {
      return link.type === 'youtube';
    })?.name;
    const updatedValues = Object.fromEntries(
      Object.entries({
        username: user.name,
        usersurname: user.surname,
        email: user.email,
        nickname: user.nickname,
        lang: user.language,
        website,
        instagram,
        youtube,
      }).filter(([, value]) => {
        return !!value;
      }),
    );
    return Promise.all([
      authenticatedRequest({
        url: `/users/${userId}`,
        method: 'put',
        data: updatedValues,
      }),
      user.country
        ? authenticatedRequest({
            url: '/v2/settings/country',
            method: 'post',
            data: { country: user.country },
          })
        : Promise.resolve(),
    ]).then(() => {
      emitter?.dispatchEvent('user-update', {
        ...currentUser,
        name: user.name || currentUser?.name,
        surname: user.surname || currentUser?.surname,
        email: user.email || currentUser?.email,
        nickname: user.nickname || currentUser?.nickname,
        language: user.language || currentUser?.language,
        country: user.country || currentUser?.country,
      } as UserType);
    });
  };

  const getProviders = (): Promise<
    {
      name: string;
      provider: string;
      products: number;
    }[]
  > => {
    return authenticatedRequest({
      url: '/v2/wallet/providers',
    }).then(({ currentCountry, providers }) => {
      return providers[currentCountry];
    });
  };

  const graphqlRequest = getGraphlClient({ token, locale });

  return {
    checkNicknameAvailability,
    getCurrentUser,
    getCurrentUserCollections,
    getUserIdByNickname,
    getUserInfo,
    getUserProfile,
    getUserProfileByNickname,
    getUserCollections,
    getCollection,
    getCollectionContent,
    getProductBasic,
    getProduct,
    getProductList,
    getUserList,
    search,
    searchByCategory,
    exploreNear,
    getNotifications,
    getUserActivity,
    getGamificationProgress,
    isMonetizableURL,
    getConversations,
    getConversation,
    getUserRelations,
    getProviders,
    // mutations
    createProduct,
    createConversation,
    createRecommendation,
    toggleFollow,
    createCollection,
    like,
    addProductToCollection,
    removeProductFromCollection,
    markNotificationsAsRead,
    sendMessage,
    markConversationAsRead,
    updateProfile,

    // New Namespace (using graphql)
    User: User({ graphqlRequest }),
    Category: Category({ graphqlRequest }),
    Product: Product({ graphqlRequest }),
    Recommendation: Recommendation({ graphqlRequest }),
    graphqlRequest,
  };
};

API.getUserByToken = async (token): Promise<undefined | UserType> => {
  if (!token) {
    return undefined;
  }
  try {
    const [{ user }, { userLocations: locations }, { country }] = await Promise.all([
      request({
        url: '/users/me',
        token,
      }),
      request({
        url: '/users/me/locations',
        token,
      }),
      request({
        url: '/v2/settings/country',
        token,
      }),
    ]);
    return {
      ...parsers.V1.user(user),
      country,
      geoHashes: locations
        .map((location: any) => {
          return location.geohash;
        })
        .filter(Boolean),
    } as UserType;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};
